<script setup lang="ts">
// @ts-nocheck
import { ref, computed, nextTick } from 'vue'
import { useConvexQuery, useConvexMutation } from 'convex-vue'
import { api } from '~/convex/_generated/api'
import type { Id } from '~/convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ThumbsUp, ThumbsDown, CheckCircle, X } from 'lucide-vue-next'

const props = defineProps<{
  messageId: string
  userId: string
}>()

// Check if user has already submitted feedback
const { data: existingFeedback } = useConvexQuery(
  api.aiFeedback.getFeedbackForMessage,
  computed(() => ({
    messageId: props.messageId as Id<'chatMessages'>,
    userId: props.userId as Id<'users'>
  }))
)

// State
const showCorrectionField = ref(false)
const correctionText = ref('')
const isSubmitting = ref(false)
const justSubmitted = ref(false)
const toastMessage = ref('')
const showToast = ref(false)

// Mutations
const { mutate: submitFeedbackMutation } = useConvexMutation(api.aiFeedback.submitFeedback)

// Check if feedback already exists
const hasFeedback = computed(() => {
  return existingFeedback.value !== null && existingFeedback.value !== undefined
})

const feedbackType = computed(() => {
  if (!existingFeedback.value) return null
  return existingFeedback.value.isPositive ? 'positive' : 'negative'
})

// Simple toast function
function showToastMessage(message: string, isError = false) {
  toastMessage.value = message
  showToast.value = true

  setTimeout(() => {
    showToast.value = false
  }, isError ? 5000 : 3000)
}

// Handle thumbs up
async function handleThumbsUp() {
  if (hasFeedback.value || isSubmitting.value) return

  isSubmitting.value = true
  try {
    await submitFeedbackMutation({
      messageId: props.messageId as Id<'chatMessages'>,
      userId: props.userId as Id<'users'>,
      isPositive: true
    })

    justSubmitted.value = true
    showToastMessage('Thanks for your feedback!')

    // Reset after animation
    setTimeout(() => {
      justSubmitted.value = false
    }, 3000)
  } catch (error: any) {
    showToastMessage(error.message || 'Failed to submit feedback. Please try again later.', true)
  } finally {
    isSubmitting.value = false
  }
}

// Handle thumbs down
function handleThumbsDown() {
  if (hasFeedback.value || isSubmitting.value) return
  showCorrectionField.value = true

  // Auto-focus textarea
  nextTick(() => {
    const textarea = document.getElementById(`correction-${props.messageId}`)
    if (textarea) {
      textarea.focus()
    }
  })
}

// Submit negative feedback
async function submitNegativeFeedback() {
  if (isSubmitting.value) return

  isSubmitting.value = true
  try {
    await submitFeedbackMutation({
      messageId: props.messageId as Id<'chatMessages'>,
      userId: props.userId as Id<'users'>,
      isPositive: false,
      correctionText: correctionText.value || undefined
    })

    justSubmitted.value = true
    showCorrectionField.value = false
    correctionText.value = ''

    showToastMessage('Thanks for your feedback! Your input helps us improve.')

    // Reset after animation
    setTimeout(() => {
      justSubmitted.value = false
    }, 3000)
  } catch (error: any) {
    showToastMessage(error.message || 'Failed to submit feedback. Please try again later.', true)
  } finally {
    isSubmitting.value = false
  }
}

// Cancel negative feedback
function cancelNegativeFeedback() {
  showCorrectionField.value = false
  correctionText.value = ''
}
</script>

<template>
  <div class="mt-2 space-y-2">
    <!-- Toast notification -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="showToast"
        class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5 bg-green-50 dark:bg-green-950/30 px-2 py-1.5 rounded-md"
      >
        <CheckCircle class="w-3 h-3" />
        <span>{{ toastMessage }}</span>
      </div>
    </Transition>

    <!-- Success Message -->
    <div
      v-if="justSubmitted && !showToast"
      class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"
    >
      <CheckCircle class="w-3 h-3" />
      <span>Thanks for your feedback!</span>
    </div>

    <!-- Feedback Buttons -->
    <div
      v-if="!showCorrectionField && !justSubmitted"
      class="flex items-center gap-2 abba-feedback-buttons"
    >
      <!-- Thumbs Up -->
      <button
        :disabled="hasFeedback || isSubmitting"
        class="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :class="feedbackType === 'positive' ? 'text-green-600' : 'text-muted-foreground hover:text-foreground'"
        title="Helpful response"
        @click="handleThumbsUp"
      >
        <ThumbsUp
          class="w-4 h-4"
          :class="feedbackType === 'positive' ? 'fill-current' : ''"
        />
      </button>

      <!-- Thumbs Down -->
      <button
        :disabled="hasFeedback || isSubmitting"
        class="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :class="feedbackType === 'negative' ? 'text-red-600' : 'text-muted-foreground hover:text-foreground'"
        title="Not helpful"
        @click="handleThumbsDown"
      >
        <ThumbsDown
          class="w-4 h-4"
          :class="feedbackType === 'negative' ? 'fill-current' : ''"
        />
      </button>

      <!-- Submitted indicator -->
      <span
        v-if="hasFeedback"
        class="text-xs text-muted-foreground"
      >
        ({{ feedbackType === 'positive' ? 'Marked helpful' : 'Feedback submitted' }})
      </span>
    </div>

    <!-- Correction Text Field -->
    <div
      v-if="showCorrectionField"
      class="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300"
    >
      <label
        :for="`correction-${messageId}`"
        class="text-xs text-muted-foreground"
      >
        What would be a better response? (optional)
      </label>
      <Textarea
        :id="`correction-${messageId}`"
        v-model="correctionText"
        placeholder="Enter the correct answer or suggest improvements..."
        :rows="3"
        class="text-sm"
        :disabled="isSubmitting"
      />
      <div class="flex gap-2">
        <Button
          size="sm"
          :disabled="isSubmitting"
          @click="submitNegativeFeedback"
        >
          Submit Feedback
        </Button>
        <Button
          size="sm"
          variant="ghost"
          :disabled="isSubmitting"
          @click="cancelNegativeFeedback"
        >
          Cancel
        </Button>
      </div>
    </div>
  </div>
</template>
